interface RateLimitEntry {
  attempts: number;
  firstAttempt: number;
  lockedUntil?: number;
}

class RateLimiter {
  private attempts: Map<string, RateLimitEntry> = new Map();
  private readonly maxAttempts = 5;
  private readonly windowMs = 15 * 60 * 1000; // 15 minutes
  private readonly lockoutMs = 30 * 60 * 1000; // 30 minutes lockout

  checkRateLimit(identifier: string): { allowed: boolean; remainingAttempts?: number; lockedUntil?: Date } {
    const now = Date.now();
    const entry = this.attempts.get(identifier);

    if (!entry) {
      return { allowed: true, remainingAttempts: this.maxAttempts };
    }

    // Check if currently locked out
    if (entry.lockedUntil && entry.lockedUntil > now) {
      return {
        allowed: false,
        lockedUntil: new Date(entry.lockedUntil)
      };
    }

    // Reset if window has passed
    if (now - entry.firstAttempt > this.windowMs) {
      this.attempts.delete(identifier);
      return { allowed: true, remainingAttempts: this.maxAttempts };
    }

    // Check if max attempts exceeded
    if (entry.attempts >= this.maxAttempts) {
      const lockedUntil = entry.firstAttempt + this.lockoutMs;
      this.attempts.set(identifier, { ...entry, lockedUntil });
      return {
        allowed: false,
        lockedUntil: new Date(lockedUntil)
      };
    }

    return {
      allowed: true,
      remainingAttempts: this.maxAttempts - entry.attempts
    };
  }

  recordAttempt(identifier: string, success: boolean): void {
    const now = Date.now();
    const entry = this.attempts.get(identifier);

    if (success) {
      // Clear on successful login
      this.attempts.delete(identifier);
      return;
    }

    if (!entry) {
      this.attempts.set(identifier, {
        attempts: 1,
        firstAttempt: now
      });
    } else {
      // Reset if window has passed
      if (now - entry.firstAttempt > this.windowMs) {
        this.attempts.set(identifier, {
          attempts: 1,
          firstAttempt: now
        });
      } else {
        this.attempts.set(identifier, {
          ...entry,
          attempts: entry.attempts + 1
        });
      }
    }
  }

  getRemainingTime(identifier: string): number | null {
    const entry = this.attempts.get(identifier);
    if (!entry?.lockedUntil) return null;

    const remaining = entry.lockedUntil - Date.now();
    return remaining > 0 ? remaining : null;
  }

  // Cleanup old entries periodically
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.attempts.entries()) {
      if (now - entry.firstAttempt > this.lockoutMs) {
        this.attempts.delete(key);
      }
    }
  }
}

export const loginRateLimiter = new RateLimiter();

// Cleanup every 10 minutes
setInterval(() => loginRateLimiter.cleanup(), 10 * 60 * 1000);
